import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowLeft, Download, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Bucket, StoredFile } from '@storeflare/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BucketSettings } from '@/components/BucketSettings';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState, Page } from '@/components/Page';
import { api } from '@/lib/api';

type UploadState =
  | { status: 'idle' }
  | { status: 'uploading'; current: number; total: number; filename: string; percent: number }
  | { status: 'error'; message: string };

type Tab = 'files' | 'settings';

export function BucketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<StoredFile | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [picked, setPicked] = useState<File[]>([]);
  const [upload, setUpload] = useState<UploadState>({ status: 'idle' });
  const [tab, setTab] = useState<Tab>('files');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Holds the in-flight XHR so the Cancel button (and dialog close) can abort
  // it. Cleared after the upload settles (success, error, or abort).
  const activeXhr = useRef<XMLHttpRequest | null>(null);
  const cancelledRef = useRef(false);

  const bucket = useQuery({
    queryKey: ['bucket', id],
    queryFn: () => api<Bucket>(`/v1/buckets/${id}`),
    enabled: !!id,
  });

  const files = useQuery({
    queryKey: ['bucket', id, 'files'],
    queryFn: () => api<StoredFile[]>(`/v1/buckets/${id}/files?limit=100`),
    enabled: !!id,
  });

  const remove = useMutation({
    mutationFn: (fileId: string) =>
      api<{ id: string; status: string }>(`/v1/files/${fileId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bucket', id, 'files'] }),
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to delete file');
    },
  });

  function onDownload(fileId: string) {
    // Goes through the Worker proxy — same-origin to our API, so the browser
    // streams it directly and there's no CORS preflight to R2.
    const base = (import.meta.env['VITE_API_BASE'] ?? '/api').replace(/\/$/, '');
    window.open(`${base}/v1/files/${fileId}/content`, '_blank', 'noopener,noreferrer');
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    const filename = pendingDelete.filename;
    try {
      await remove.mutateAsync(pendingDelete.id);
      toast.success(`Deleted "${filename}"`);
    } catch {
      // onError handles the toast
    }
    setPendingDelete(null);
  }

  function resetUpload() {
    setPicked([]);
    setUpload({ status: 'idle' });
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function openUpload() {
    resetUpload();
    setUploadOpen(true);
  }

  function cancelInFlight() {
    if (!activeXhr.current) return;
    cancelledRef.current = true;
    activeXhr.current.abort();
    activeXhr.current = null;
  }

  function closeUpload() {
    cancelInFlight();
    setUploadOpen(false);
    resetUpload();
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const list = e.target.files;
    if (!list) return;
    addFiles(Array.from(list));
  }

  function addFiles(incoming: File[]) {
    if (incoming.length === 0) return;
    setPicked((prev) => {
      // Dedup by name+size+lastModified — this is what the OS uses for "same file".
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}|${f.lastModified}`));
      const next = [...prev];
      for (const f of incoming) {
        const key = `${f.name}|${f.size}|${f.lastModified}`;
        if (!seen.has(key)) {
          seen.add(key);
          next.push(f);
        }
      }
      return next;
    });
    setUpload({ status: 'idle' });
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (upload.status === 'uploading') return;
    const dropped = Array.from(e.dataTransfer.files ?? []);
    addFiles(dropped);
  }

  async function startUpload() {
    if (!id || picked.length === 0) return;
    cancelledRef.current = false;
    for (let i = 0; i < picked.length; i++) {
      const file = picked[i]!;
      setUpload({
        status: 'uploading',
        current: i + 1,
        total: picked.length,
        filename: file.name,
        percent: 0,
      });
      try {
        await uploadOne(
          id,
          file,
          (percent) =>
            setUpload({
              status: 'uploading',
              current: i + 1,
              total: picked.length,
              filename: file.name,
              percent,
            }),
          (xhr) => {
            activeXhr.current = xhr;
          },
        );
        activeXhr.current = null;
      } catch (err) {
        activeXhr.current = null;
        if (cancelledRef.current) {
          // User-initiated abort. The dialog has already been closed (or
          // about to be); leave state clean.
          return;
        }
        const message = err instanceof Error ? err.message : 'Upload failed';
        setUpload({ status: 'error', message });
        toast.error(`Upload failed: ${message}`);
        return;
      }
    }
    await qc.invalidateQueries({ queryKey: ['bucket', id, 'files'] });
    const count = picked.length;
    setUploadOpen(false);
    resetUpload();
    toast.success(count === 1 ? 'File uploaded' : `${count} files uploaded`);
  }

  const title = bucket.data?.name ?? 'Bucket';
  const subtitle = bucket.data
    ? `${bucket.data.region} · ${bucket.data.public_access ? 'public' : 'private'}`
    : undefined;

  return (
    <Page
      title={title}
      subtitle={subtitle}
      actions={
        <div className="flex gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/buckets">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              All buckets
            </Link>
          </Button>
          {tab === 'files' ? (
            <Button size="sm" onClick={openUpload}>
              <Upload className="mr-1.5 h-4 w-4" />
              Upload
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="mb-4 inline-flex rounded-md border bg-muted/30 p-0.5 text-sm">
        <TabButton active={tab === 'files'} onClick={() => setTab('files')}>
          Files
        </TabButton>
        <TabButton active={tab === 'settings'} onClick={() => setTab('settings')}>
          Settings
        </TabButton>
      </div>

      {tab === 'settings' ? (
        bucket.data ? (
          <BucketSettings bucket={bucket.data} />
        ) : (
          <Skeleton className="h-40 w-full" />
        )
      ) : files.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !files.data || files.data.length === 0 ? (
        <EmptyState title="No files in this bucket" hint="Click Upload to add your first file." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Filename</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Size</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.data.map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="max-w-[20rem] truncate font-medium">{f.filename}</TableCell>
                  <TableCell className="text-muted-foreground">{f.mime_type}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatBytes(f.size)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={f.status === 'active' ? 'secondary' : 'outline'}>
                      {f.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(f.uploaded_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={f.status !== 'active'}
                        onClick={() => onDownload(f.id)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={f.status === 'deleted'}
                        onClick={() => setPendingDelete(f)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={uploadOpen}
        onOpenChange={(open) => {
          if (!open) closeUpload();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload files</DialogTitle>
            <DialogDescription>
              Files will be uploaded to{' '}
              <span className="font-medium text-foreground">{bucket.data?.name ?? 'this bucket'}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                if (upload.status !== 'uploading') setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => {
                if (upload.status !== 'uploading') fileInputRef.current?.click();
              }}
              role="button"
              tabIndex={0}
              className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-6 text-center text-sm transition-colors ${
                dragOver
                  ? 'border-primary bg-primary/5 text-foreground'
                  : 'border-input bg-muted/20 text-muted-foreground hover:bg-muted/40'
              } ${upload.status === 'uploading' ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <Upload className="h-5 w-5" />
              <div>
                <span className="font-medium text-foreground">Click to browse</span>
                <span> or drag &amp; drop files here</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={onPick}
                disabled={upload.status === 'uploading'}
                className="hidden"
              />
            </div>

            {picked.length > 0 && upload.status !== 'uploading' ? (
              <ul className="max-h-40 space-y-1 overflow-auto rounded-md border bg-muted/30 p-2 text-sm">
                {picked.map((f, idx) => (
                  <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2">
                    <span className="truncate">{f.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="tabular-nums text-xs text-muted-foreground">
                        {formatBytes(f.size)}
                      </span>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => setPicked((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}

            {upload.status === 'uploading' ? (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="truncate text-foreground">{upload.filename}</span>
                  <span className="text-muted-foreground">
                    {upload.current}/{upload.total} · {Math.round(upload.percent * 100)}%
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full bg-primary transition-[width]"
                    style={{ width: `${Math.round(upload.percent * 100)}%` }}
                  />
                </div>
              </div>
            ) : null}

            {upload.status === 'error' ? (
              <p className="text-sm text-destructive">{upload.message}</p>
            ) : null}
          </div>

          <DialogFooter>
            {upload.status === 'uploading' ? (
              <Button variant="destructive" onClick={closeUpload}>
                Cancel upload
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeUpload}>
                  Close
                </Button>
                <Button onClick={startUpload} disabled={picked.length === 0}>
                  {`Upload ${picked.length || ''}`.trim()}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this file?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete ? (
                <>
                  <span className="font-medium text-foreground">{pendingDelete.filename}</span> will
                  be marked deleted. This can't be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={remove.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}

function uploadOne(
  bucketId: string,
  file: File,
  onProgress: (fraction: number) => void,
  onStart?: (xhr: XMLHttpRequest) => void,
): Promise<void> {
  // Single-shot proxy upload through the Worker. Metadata travels in headers
  // so the body can be streamed without buffering on either side.
  const base = (import.meta.env['VITE_API_BASE'] ?? '/api').replace(/\/$/, '');
  const url = `${base}/v1/buckets/${bucketId}/upload`;
  const mimeType = file.type || 'application/octet-stream';

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', mimeType);
    xhr.setRequestHeader('x-filename', encodeURIComponent(file.name));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    xhr.onerror = () => reject(new Error('Network error during upload.'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      // Surface the structured API error if we got one back.
      let message = `Upload failed: HTTP ${xhr.status}`;
      try {
        const envelope = JSON.parse(xhr.responseText) as {
          error?: { message?: string };
        };
        if (envelope.error?.message) message = envelope.error.message;
      } catch {
        /* response wasn't JSON */
      }
      reject(new Error(message));
    };
    onStart?.(xhr);
    xhr.send(file);
  });
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-3 py-1 transition-colors ${
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}
